import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { IoShareSocialOutline, IoLinkOutline, IoCopyOutline, IoQrCodeOutline, IoDownloadOutline } from 'react-icons/io5';
import QRCode from 'qrcode';
import { setPackagingUnitPublicPage, generatePackagingUnitQrCode, downloadQrCodePdf } from '../../../../store/actions/packagingUnit';

const Section = ({ icon: Icon, title, children }) => (
    <div className="p-3 d-flex flex-column" style={{ backgroundColor: '#ffffff', border: '1px solid #E5E7F2', borderRadius: 14 }}>
        <div className="d-flex align-items-center gap-2 pb-2 mb-2" style={{ borderBottom: '1px solid #EEF0F8' }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, backgroundColor: '#EEF1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={14} style={{ color: '#0B1157' }} />
            </div>
            <span className="fw-semibold" style={{ color: '#0B1157', fontSize: 13 }}>{title}</span>
        </div>
        <div>{children}</div>
    </div>
);

// Verejná stránka obalu + jej QR kód (Označenie obalu) - publicId aj raz vygenerovaný QR kód sú
// natrvalo fixné, rovnako ako verejná URL obalovej jednotky (viď model PackagingUnit).
const PublicPageSection = ({ unit }) => {
    const dispatch = useDispatch();

    const [togglingPublicPage, setTogglingPublicPage] = useState(false);
    const [copied, setCopied] = useState(false);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
    const [generatingQrCode, setGeneratingQrCode] = useState(false);
    const [downloadingQrCodePdf, setDownloadingQrCodePdf] = useState(false);

    const publicPageUrl = `${window.location.origin}/public/packaging/${unit.tenant}/${unit.publicId}`;

    useEffect(() => {
        if (unit.qrCodeGeneratedAt) {
            QRCode.toDataURL(publicPageUrl, { width: 320, margin: 1, color: { dark: '#0B1157', light: '#FFFFFF' } })
                .then(setQrCodeDataUrl)
                .catch(() => {});
        } else {
            setQrCodeDataUrl(null);
        }
    }, [unit.qrCodeGeneratedAt, publicPageUrl]);

    const handleTogglePublicPage = (event) => {
        const enabled = event.target.checked;
        setTogglingPublicPage(true);
        dispatch(setPackagingUnitPublicPage({ packagingUnitId: unit._id, enabled }))
            .unwrap()
            .catch(() => {})
            .finally(() => setTogglingPublicPage(false));
    };

    const handleCopyPublicUrl = () => {
        navigator.clipboard.writeText(publicPageUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const handleGenerateQrCode = () => {
        if (unit.qrCodeGeneratedAt) return;
        setGeneratingQrCode(true);
        dispatch(generatePackagingUnitQrCode({ packagingUnitId: unit._id }))
            .unwrap()
            .catch(() => {})
            .finally(() => setGeneratingQrCode(false));
    };

    const handleDownloadQrCode = () => {
        if (!qrCodeDataUrl) return;
        const link = document.createElement('a');
        link.href = qrCodeDataUrl;
        link.download = `QR-${(unit.code || unit.name || 'obal').replace(/[^a-zA-Z0-9-_]+/g, '_')}.png`;
        link.click();
    };

    const handleDownloadQrCodePdf = () => {
        setDownloadingQrCodePdf(true);
        dispatch(downloadQrCodePdf({ packagingUnitId: unit._id, name: unit.code || unit.name }))
            .unwrap()
            .catch(() => {})
            .finally(() => setDownloadingQrCodePdf(false));
    };

    return (
        <div className="mb-3">
            <Section icon={IoShareSocialOutline} title="Verejná stránka obalu">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div style={{ maxWidth: 620, fontSize: 13, color: '#5B6178' }}>
                        Po zapnutí bude táto stránka s informáciami o obale dostupná komukoľvek na odkaze nižšie,
                        aj bez prihlásenia - napr. pre spotrebiteľov cez QR kód vytlačený na obale.
                    </div>
                    <Form.Check
                        type="switch"
                        id="public-page-switch"
                        checked={!!unit.publicPageEnabled}
                        disabled={togglingPublicPage}
                        onChange={handleTogglePublicPage}
                        label={unit.publicPageEnabled ? 'Zapnuté' : 'Vypnuté'}
                    />
                </div>
                {unit.publicPageEnabled && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px solid #EEF0F8' }}>
                        <IoLinkOutline size={13} style={{ color: '#0B1157', flexShrink: 0 }} />
                        <a href={publicPageUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, wordBreak: 'break-all' }}>
                            {publicPageUrl}
                        </a>
                        <Button className="btnIcon" onClick={handleCopyPublicUrl} title="Kopírovať odkaz">
                            <IoCopyOutline className="icon" size={16} />
                        </Button>
                        {copied && <span className="text-success" style={{ fontSize: 12 }}>Skopírované</span>}
                    </div>
                )}
                {unit.publicPageEnabled && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #EEF0F8' }}>
                        {!unit.qrCodeGeneratedAt ? (
                            <Button
                                className="btn-custom-small d-inline-flex align-items-center gap-1"
                                disabled={generatingQrCode}
                                onClick={handleGenerateQrCode}
                            >
                                <IoQrCodeOutline size={15} />
                                {generatingQrCode ? 'Generuje sa...' : 'Vygenerovať QR kód'}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    className="btn-custom-small d-inline-flex align-items-center gap-1 mb-2"
                                    disabled={downloadingQrCodePdf}
                                    onClick={handleDownloadQrCodePdf}
                                    title="Stiahnuť QR kód ako vektorové PDF (krivky) - vhodné pre tlač/výrobu obalu"
                                >
                                    <IoDownloadOutline size={15} />
                                    {downloadingQrCodePdf ? 'Sťahuje sa...' : 'Stiahnuť PDF (krivky)'}
                                </Button>
                                {qrCodeDataUrl && (
                                    <div className="d-flex align-items-center gap-2">
                                        <img
                                            src={qrCodeDataUrl}
                                            alt="QR kód na verejnú stránku obalu"
                                            style={{ width: 140, height: 140, borderRadius: 10, border: '1px solid #E5E7F2' }}
                                        />
                                        <Button className="btnIcon" onClick={handleDownloadQrCode} title="Stiahnuť QR kód (PNG)">
                                            <IoDownloadOutline className="icon" size={16} />
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </Section>
        </div>
    );
};

export default PublicPageSection;
